import React, { useEffect, useState, Suspense, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import "./ManagerDashboard.css";

// Lazy-loaded components
const LeaveManagementManager = React.lazy(() => import("./LeaveManagementManager"));
const AttendanceManagementManager = React.lazy(() => import("./AttendanceManagementManager"));
const TimesheetManager = React.lazy(() => import("./TimesheetManager"));
const TeamManagementManager = React.lazy(() => import("./TeamManagementManager"));
const TaskManagementManager = React.lazy(() => import("./TaskManagementManager"));
const ManagerMeetings = React.lazy(() => import("./ManagerMeetings"));
const ManagerLeaveApplication = React.lazy(() => import("./ManagerLeaveApplication"));
const ManagerChats = React.lazy(() => import("./ManagerChats"));

// Custom hook for manager auth
const useManagerAuth = (navigate) => {
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("🔄 [Auth] Starting authentication check...");
    
    const token = sessionStorage.getItem("managerToken");
    if (!token) {
      console.log("❌ [Auth] No token found, redirecting to login");
      navigate("/managerlogin");
      return;
    }

    const storedManager = sessionStorage.getItem("managerData");
    if (storedManager) {
      try {
        const parsedManager = JSON.parse(storedManager);
        console.log("✅ [Auth] Using stored manager:", parsedManager.name);
        console.log("✅ [Auth] Manager ID:", parsedManager._id);
        setManager(parsedManager);
        setLoading(false);
        return;
      } catch (err) {
        console.error("❌ [Auth] Error parsing stored manager:", err);
      }
    }

    const fetchManagerData = async () => {
      try {
        console.log("🔄 [Auth] Fetching manager data...");
        
        let managerId = null;
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const tokenPayload = JSON.parse(atob(tokenParts[1]));
            managerId = tokenPayload.id || tokenPayload.userId;
            console.log("✅ [Auth] Manager ID from token:", managerId);
          }
        } catch (decodeError) {
          console.log("⚠️ [Auth] Could not decode token:", decodeError.message);
        }

        let managerData = null;
        let endpointTried = "";
        
        // Method 1: Try /api/managers/me
        try {
          endpointTried = "/api/managers/me";
          console.log(`🔄 [Auth] Trying endpoint: ${endpointTried}`);
          const response = await fetch(`${API_BASE_URL}${endpointTried}`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            managerData = await response.json();
            console.log("✅ [Auth] Got manager from /me endpoint");
          } else {
            console.log(`⚠️ [Auth] ${endpointTried} failed with status: ${response.status}`);
          }
        } catch (error) {
          console.log(`⚠️ [Auth] ${endpointTried} endpoint failed:`, error.message);
        }
        
        // Method 2: Try by ID if we have it
        if (!managerData && managerId) {
          try {
            endpointTried = `/api/managers/${managerId}`;
            console.log(`🔄 [Auth] Trying endpoint: ${endpointTried}`);
            const response = await fetch(`${API_BASE_URL}${endpointTried}`, {
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              managerData = await response.json();
              console.log("✅ [Auth] Got manager by ID");
            } else {
              console.log(`⚠️ [Auth] ${endpointTried} failed with status: ${response.status}`);
            }
          } catch (error) {
            console.log(`⚠️ [Auth] ${endpointTried} endpoint failed:`, error.message);
          }
        }
        
        // Method 3: Try /api/managers/profile
        if (!managerData) {
          try {
            endpointTried = "/api/managers/profile";
            console.log(`🔄 [Auth] Trying endpoint: ${endpointTried}`);
            const response = await fetch(`${API_BASE_URL}${endpointTried}`, {
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              managerData = await response.json();
              console.log("✅ [Auth] Got manager from profile endpoint");
            } else {
              console.log(`⚠️ [Auth] ${endpointTried} failed with status: ${response.status}`);
            }
          } catch (error) {
            console.log(`⚠️ [Auth] ${endpointTried} endpoint failed:`, error.message);
          }
        }

        if (managerData) {
          console.log("✅ [Auth] Manager data fetched:", managerData.name);
          console.log("✅ [Auth] Manager ID from API:", managerData._id);
          setManager(managerData);
          sessionStorage.setItem("managerData", JSON.stringify(managerData));
        } else {
          console.error("❌ [Auth] Could not fetch manager data from any endpoint");
          console.log("ℹ️ [Auth] Trying to create manager from token data...");
          
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const tokenPayload = JSON.parse(atob(tokenParts[1]));
              const tempManager = {
                _id: tokenPayload.id || `temp-${Date.now()}`,
                name: tokenPayload.name || "Manager",
                email: tokenPayload.email || "",
                company_name: tokenPayload.company_name || "Unknown Company",
                department: tokenPayload.department || "Management"
              };
              console.log("✅ [Auth] Created temporary manager from token");
              setManager(tempManager);
              sessionStorage.setItem("managerData", JSON.stringify(tempManager));
            }
          } catch (createError) {
            console.error("❌ [Auth] Failed to create manager from token:", createError);
            sessionStorage.clear();
            navigate("/managerlogin");
          }
        }
        
      } catch (error) {
        console.error("❌ [Auth] Error fetching manager:", error);
        sessionStorage.clear();
        navigate("/managerlogin");
      } finally {
        setLoading(false);
      }
    };

    fetchManagerData();
  }, [navigate]);

  return { manager, loading, setManager };
};

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { manager, loading, setManager } = useManagerAuth(navigate);
  const fileInputRef = useRef(null);

  // Create refs to prevent duplicate API calls
  const hasFetchedTimesheets = useRef(false);
  const hasFetchedLeaves = useRef(false);
  const hasFetchedEmployees = useRef(false);
  const isFetching = useRef(false);

  const [activeSection, setActiveSection] = useState("overview");
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [employeePerformance, setEmployeePerformance] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);
  const [profileImages, setProfileImages] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add state to track if we're stuck
  const [isTabSwitchBlocked, setIsTabSwitchBlocked] = useState(false);
  const [lastActiveTab, setLastActiveTab] = useState("overview");

  // Stats for dashboard
  const [stats, setStats] = useState({
    totalTeam: 0,
    pendingLeave: 0,
    presentToday: 0,
    completedTasks: 0
  });

  // Sidebar menu items
  const menuItems = [
    {
      category: "Dashboard",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      items: [
        { key: "overview", label: "Overview" },
        { key: "profile", label: "My Profile" }
      ]
    },
    {
      category: "Team Management",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13 0a5 5 0 00-7.07-7.07" />
        </svg>
      ),
      items: [
        { key: "team", label: "Team" },
        { key: "attendance", label: "Attendance" },
        { key: "timesheets", label: "Timesheets" }
      ]
    },
    {
      category: "Work Management",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      items: [
        { key: "leave", label: "Leave Requests" },
        { key: "tasks", label: "Tasks" },
        { key: "manager-leaves", label: "My Leaves" }
      ]
    },
    {
      category: "Communication",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      items: [
        { key: "meetings", label: "Meetings" },
        // { key: "chats", label: "Team Chat" }
      ]
    }
  ];

  // Debug manager state on every change
  useEffect(() => {
    console.log("🔄 [Dashboard] Manager state updated:", {
      hasManager: !!manager,
      managerId: manager?._id,
      managerName: manager?.name,
      loading: loading
    });
  }, [manager, loading]);

  // Helper function to get profile image URL
  const getProfileImageUrl = (manager) => {
    if (!manager) return null;
    
    if (manager.profileImageUrl) {
      return `${API_BASE_URL}${manager.profileImageUrl}?t=${Date.now()}&v=${imageVersion}`;
    }
    
    if (manager.profileImage && manager.profileImage.data) {
      return `${API_BASE_URL}/api/managers/${manager._id}/profile-image?t=${Date.now()}&v=${imageVersion}`;
    }
    
    return null;
  };

  // Fetch employee profile images
  const fetchEmployeeProfileImages = useCallback(async (employees) => {
    try {
      console.log("🔄 [Profile Images] Fetching for", employees.length, "employees");
      const images = {};
      const token = sessionStorage.getItem("managerToken");
      
      if (!token) {
        console.error("❌ [Profile Images] No manager token found");
        return;
      }
      
      for (const emp of employees) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/profiles/${emp._id}`, {
            headers: { 
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });
          
          if (response.ok) {
            const profileData = await response.json();
            const imageUrl = profileData.profilePic || profileData.profilePicture || profileData.photo;
            
            if (imageUrl) {
              if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                images[emp._id] = `${API_BASE_URL}${imageUrl}`;
              } else {
                images[emp._id] = imageUrl;
              }
            }
          }
        } catch (err) {
          console.log(`🚫 [Profile Images] Error for ${emp.name}:`, err.message);
        }
      }
      
      setProfileImages(images);
      
    } catch (error) {
      console.error("❌ [Profile Images] Error:", error);
    }
  }, []);

  // Fetch assigned employees
  const fetchAssignedEmployees = useCallback(async () => {
    console.log("🔄 [Team] fetchAssignedEmployees called");
    
    if (!manager || !manager._id || !manager.company_name) {
      console.log("⏳ [Team] Skipping - manager not ready:", {
        hasManager: !!manager,
        managerId: manager?._id,
        companyName: manager?.company_name
      });
      return;
    }

    if (hasFetchedEmployees.current === manager._id) {
      console.log("⏳ [Team] Already fetched for this manager, skipping");
      return;
    }

    try {
      console.log(`🔍 [Team] Fetching employees for company: ${manager.company_name}`);
      
      const token = sessionStorage.getItem("managerToken");
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await axios.get(
        `${API_BASE_URL}/api/employees/company/${manager.company_name}`,
        { 
          headers,
          timeout: 10000,
          validateStatus: (status) => status >= 200 && status < 500
        }
      );
      
      console.log("📦 [Team] Response received:", {
        status: res.status,
        dataType: typeof res.data,
        hasData: !!res.data
      });

      if (res.data && Array.isArray(res.data)) {
        const employees = res.data;
        console.log(`✅ [Team] Found ${employees.length} total employees`);
        
        const managerEmployees = employees.filter(emp => {
          const reportingManager = emp.reporting_manager;
          return (
            reportingManager === manager.name ||
            reportingManager === manager._id ||
            reportingManager === manager.email ||
            reportingManager === manager.emp_id ||
            (reportingManager && reportingManager.name === manager.name)
          );
        });
        
        console.log(`✅ [Team] ${managerEmployees.length} employees assigned to manager`);
        setAssignedEmployees(managerEmployees);
        setStats(prev => ({ ...prev, totalTeam: managerEmployees.length }));
        
        await fetchEmployeeProfileImages(managerEmployees);
        
        if (managerEmployees.length > 0) {
          fetchEmployeePerformance(managerEmployees);
        }
        
        hasFetchedEmployees.current = manager._id;
      } else {
        console.log("⚠️ [Team] No employee data or invalid format");
        setAssignedEmployees([]);
      }
    } catch (err) {
      console.error("❌ [Team] Error fetching employees:", err.message);
      setAssignedEmployees([]);
    }
  }, [manager, fetchEmployeeProfileImages]);

  // Fetch employee performance
  const fetchEmployeePerformance = useCallback(async (employees) => {
    if (!manager || !employees || employees.length === 0) return;

    try {
      console.log("🔍 [Performance] Fetching performance data");
      const performanceData = {};
      let totalCompletedTasks = 0;

      for (const employee of employees) {
        if (!employee.emp_id && !employee._id) continue;

        try {
          let endpoint = employee.emp_id 
            ? `${API_BASE_URL}/api/tasks/performance/${employee.emp_id}`
            : `${API_BASE_URL}/api/tasks/performance/employee/${employee._id}`;

          const res = await axios.get(endpoint, {
            timeout: 5000,
            validateStatus: (status) => status >= 200 && status < 500
          });
          
          if (res.data && res.data.success) {
            const empData = res.data.data || {};
            
            performanceData[employee._id] = {
              ...empData,
              name: employee.name,
              email: employee.email || employee.company_email,
              emp_id: employee.emp_id,
              appreciation: (empData.percentage || 0) >= 80,
              needsImprovement: (empData.percentage || 0) < 50,
              percentage: empData.percentage || 0,
              completed: empData.completed || 0,
              inProgress: empData.inProgress || 0,
              pending: empData.pending || 0,
              total: empData.totalTasks || 0
            };

            totalCompletedTasks += empData.completed || 0;
          }
        } catch (err) {
          console.log(`⚠️ [Performance] No data for ${employee.name}:`, err.message);
          performanceData[employee._id] = {
            name: employee.name,
            email: employee.email || employee.company_email,
            emp_id: employee.emp_id,
            appreciation: false,
            needsImprovement: false,
            percentage: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            total: 0
          };
        }
      }

      console.log("✅ [Performance] Data prepared:", Object.keys(performanceData).length, "employees");
      setEmployeePerformance(performanceData);
      setStats(prev => ({ ...prev, completedTasks: totalCompletedTasks }));

    } catch (err) {
      console.error("❌ [Performance] Error:", err);
    }
  }, [manager]);

  // Fetch attendance data for dashboard stats
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!manager || !assignedEmployees || assignedEmployees.length === 0) {
        console.log("⏳ [Attendance] Skipping - no manager or employees");
        return;
      }

      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await axios.get(`${API_BASE_URL}/api/attendance/team`, {
          params: { 
            date: today,
            managerId: manager._id
          },
          timeout: 10000,
          validateStatus: (status) => status >= 200 && status < 500
        });

        if (response.data && response.data.success) {
          const summary = response.data.summary || {};
          setStats(prev => ({ 
            ...prev, 
            presentToday: summary.present || 0
          }));
          
          setAttendanceData(response.data.teamAttendance || []);
          console.log(`✅ [Attendance] Present today: ${summary.present || 0}`);
        }
      } catch (err) {
        console.error("⚠️ [Attendance] Error fetching data:", err.message);
        setStats(prev => ({ 
          ...prev, 
          presentToday: 0
        }));
      }
    };
    
    if (manager && manager._id && assignedEmployees.length > 0) {
      fetchAttendanceData();
    }
  }, [assignedEmployees, manager]);

  // Fetch team timesheets
  useEffect(() => {
    console.log("🔄 [Timesheet Effect] Running...");
    
    const fetchTeamTimesheets = async () => {
      if (!manager || !manager._id || manager._id === 'undefined' || manager._id === 'null') {
        console.log("⏳ [Timesheet] Skipping - manager not ready");
        setTimesheets([]);
        return;
      }

      if (hasFetchedTimesheets.current === manager._id && timesheets.length > 0) {
        console.log("⏳ [Timesheet] Already fetched for this manager, skipping");
        return;
      }

      console.log(`🚀 [Timesheet] Starting fetch for manager: ${manager._id}`);
      setTimesheetLoading(true);
      
      try {
        const token = sessionStorage.getItem("managerToken");
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await axios.get(
          `${API_BASE_URL}/api/timesheets/team/${manager._id}`,
          {
            headers,
            timeout: 10000,
            validateStatus: function (status) {
              return status >= 200 && status < 500;
            }
          }
        );
        
        console.log("📦 [Timesheet] Response received:", {
          status: response.status,
          dataType: typeof response.data,
          hasData: !!response.data
        });

        let timesheetData = [];
        
        if (response.data) {
          if (response.data.success) {
            timesheetData = response.data.timesheets || response.data.data || [];
            console.log(`✅ [Timesheet] Success: Found ${timesheetData.length} timesheets`);
          } 
          else if (Array.isArray(response.data)) {
            timesheetData = response.data;
            console.log(`✅ [Timesheet] Array response: Found ${timesheetData.length} timesheets`);
          }
          else if (response.status === 400) {
            console.warn("⚠️ [Timesheet] Bad request - check manager ID");
          }
          else if (response.status === 401) {
            console.error("🔒 [Timesheet] Unauthorized - token may be invalid");
          }
          else if (response.status === 404) {
            console.log("📭 [Timesheet] No timesheets found for this manager");
          }
          else {
            console.log("ℹ️ [Timesheet] Unexpected format:", response.data);
          }
        }

        setTimesheets(timesheetData);
        hasFetchedTimesheets.current = manager._id;
        
      } catch (error) {
        console.error("❌ [Timesheet] Fetch error:", {
          message: error.message,
          code: error.code,
          response: error.response?.data
        });
        
        if (error.code === 'ECONNABORTED') {
          console.error("⏱️ [Timesheet] Request timeout");
        } else if (error.response) {
          console.error(`❌ [Timesheet] Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error("🌐 [Timesheet] Network error - no response");
        }
        
        setTimesheets([]);
      } finally {
        setTimesheetLoading(false);
      }
    };

    const timer = setTimeout(() => {
      if (!isFetching.current) {
        isFetching.current = true;
        fetchTeamTimesheets().finally(() => {
          isFetching.current = false;
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [manager, timesheets.length]);

  // Fetch team leave requests
  useEffect(() => {
    console.log("🔄 [Leave Effect] Running...");
    
    const fetchTeamLeaveRequests = async () => {
      if (!manager || !manager._id || manager._id === 'undefined' || manager._id === 'null') {
        console.log("⏳ [Leave] Skipping - manager not ready");
        setLeaveRequests([]);
        return;
      }

      if (hasFetchedLeaves.current === manager._id && leaveRequests.length > 0) {
        console.log("⏳ [Leave] Already fetched for this manager, skipping");
        return;
      }

      console.log(`🚀 [Leave] Starting fetch for manager: ${manager._id}`);
      setLeaveLoading(true);
      
      try {
        const token = sessionStorage.getItem("managerToken");
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await axios.get(
          `${API_BASE_URL}/api/leave/team-requests/${manager._id}`,
          {
            headers,
            timeout: 10000,
            validateStatus: function (status) {
              return status >= 200 && status < 500;
            }
          }
        );
        
        console.log("📦 [Leave] Response received:", {
          status: response.status,
          dataType: typeof response.data,
          hasData: !!response.data
        });

        let leaveData = [];
        
        if (response.data) {
          if (response.data.success) {
            leaveData = response.data.leaveRequests || response.data.requests || response.data.data || [];
            console.log(`✅ [Leave] Success: Found ${leaveData.length} leave requests`);
          } 
          else if (Array.isArray(response.data)) {
            leaveData = response.data;
            console.log(`✅ [Leave] Array response: Found ${leaveData.length} leave requests`);
          }
          else if (response.status === 400) {
            console.warn("⚠️ [Leave] Bad request - check manager ID");
          }
          else if (response.status === 401) {
            console.error("🔒 [Leave] Unauthorized - token may be invalid");
          }
          else if (response.status === 404) {
            console.log("📭 [Leave] No leave requests found for this manager");
          }
        }

        setLeaveRequests(leaveData);
        hasFetchedLeaves.current = manager._id;
        
        const pendingLeaves = leaveData.filter(req => 
          req.status && req.status.toLowerCase() === "pending"
        ).length;
        
        setStats(prev => ({ 
          ...prev, 
          pendingLeave: pendingLeaves 
        }));
        
        console.log(`📊 [Leave] Pending leaves: ${pendingLeaves}`);

      } catch (error) {
        console.error("❌ [Leave] Fetch error:", {
          message: error.message,
          code: error.code,
          response: error.response?.data
        });
        
        if (error.code === 'ECONNABORTED') {
          console.error("⏱️ [Leave] Request timeout");
        } else if (error.response) {
          console.error(`❌ [Leave] Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error("🌐 [Leave] Network error - no response");
        }
        
        setLeaveRequests([]);
        setStats(prev => ({ ...prev, pendingLeave: 0 }));
        
      } finally {
        setLeaveLoading(false);
      }
    };

    const timer = setTimeout(() => {
      if (!isFetching.current) {
        isFetching.current = true;
        fetchTeamLeaveRequests().finally(() => {
          isFetching.current = false;
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [manager, leaveRequests.length]);

  // Initialize data fetching when manager is available
  useEffect(() => {
    console.log("🔄 [Init Effect] Checking if we should fetch data...");
    
    if (!loading && manager && manager._id) {
      console.log("🚀 [Init] Manager ready, starting data fetching...");
      console.log("🚀 [Init] Manager ID:", manager._id);
      console.log("🚀 [Init] Manager Name:", manager.name);
      
      if (hasFetchedEmployees.current !== manager._id) {
        fetchAssignedEmployees();
      }
      
    } else if (!loading && !manager) {
      console.log("⚠️ [Init] Manager not found after loading");
    }
  }, [loading, manager, fetchAssignedEmployees]);

  // Add global event listeners for tab switching issues
  useEffect(() => {
    const cleanupTaskModals = () => {
      // Clean up any modals or overlays that might block tab switching
      const overlays = document.querySelectorAll('.modal-overlay, .modal-backdrop');
      overlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });
      
      // Reset body styles
      document.body.style.overflow = 'auto';
      document.body.style.pointerEvents = 'auto';
    };

    // Clean up when component unmounts or when activeSection changes
    return cleanupTaskModals;
  }, [activeSection]);

  // Handle profile image upload
// Handle profile image upload - FIXED VERSION
const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    alert('Image size should be less than 5MB');
    return;
  }

  // CRITICAL: Check if manager ID is valid
  if (!manager || !manager._id || manager._id === 'undefined' || manager._id === 'null') {
    console.error('❌ [Profile Upload] Invalid manager ID:', manager?._id);
    alert('Cannot upload image: Manager ID is not valid. Please refresh the page.');
    return;
  }

  console.log('🔄 [Profile Upload] Starting upload for manager:', {
    managerId: manager._id,
    managerName: manager.name,
    fileName: file.name
  });

  const formData = new FormData();
  formData.append('profileImage', file);
  formData.append('managerId', manager._id);

  setUploadingImage(true);

  try {
    const token = sessionStorage.getItem("managerToken");
    
    const response = await axios.post(
      `${API_BASE_URL}/api/managers/upload-profile-image`, 
      formData, 
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 30000
      }
    );

    console.log('✅ [Profile Upload] Response:', response.data);

    if (response.data.success) {
      // Method 1: Use the manager data from response
      if (response.data.manager) {
        const updatedManager = response.data.manager;
        console.log('✅ [Profile Upload] Updated manager data:', updatedManager);
        
        // Update manager state with new data
        setManager(prev => ({
          ...prev,
          profileImageUrl: updatedManager.profileImageUrl,
          profileImage: updatedManager.profileImage || prev.profileImage
        }));
        
        // Update session storage
        sessionStorage.setItem("managerData", JSON.stringify({
          ...manager,
          profileImageUrl: updatedManager.profileImageUrl
        }));
        
        // Force image refresh
        setImageVersion(prev => prev + 1);
      } 
      // Method 2: If no manager in response, refetch
      else {
        await refetchManagerData();
      }
      
      alert('✅ Profile image updated successfully!');
      
    } else {
      throw new Error(response.data.message || 'Failed to upload image');
    }
  } catch (error) {
    console.error('❌ [Profile Upload] Error:', error);
    
    let errorMessage = 'Upload failed. ';
    
    if (error.response) {
      if (error.response.status === 400) {
        errorMessage += 'Invalid request. Please check the image file.';
      } else if (error.response.status === 401) {
        errorMessage += 'Session expired. Please login again.';
        sessionStorage.clear();
        navigate("/managerlogin");
      } else if (error.response.status === 404) {
        errorMessage += 'Manager not found.';
      } else {
        errorMessage += error.response.data?.message || 'Server error occurred.';
      }
    } else if (error.request) {
      errorMessage += 'Network error. Please check your connection.';
    } else {
      errorMessage += error.message || 'Unknown error occurred.';
    }
    
    alert(errorMessage);
  } finally {
    setUploadingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }
};

  // Refetch manager data
// Refetch manager data - IMPROVED VERSION
const refetchManagerData = async () => {
  try {
    console.log('🔄 [Refetch] Refetching manager data...');
    
    const token = sessionStorage.getItem("managerToken");
    if (!token) {
      console.error('❌ [Refetch] No token found');
      return;
    }

    // Try multiple endpoints to get fresh data
    let freshManagerData = null;
    
    // Method 1: Try /api/managers/me with current manager ID
    if (manager && manager._id && manager._id !== 'undefined') {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/managers/me?id=${manager._id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data && response.data.manager) {
          freshManagerData = response.data.manager;
          console.log('✅ [Refetch] Got manager from /me endpoint:', freshManagerData.name);
        }
      } catch (error) {
        console.log('⚠️ [Refetch] Could not fetch from /me:', error.message);
      }
    }
    
    // Method 2: Try by manager ID (emp_id)
    if (!freshManagerData && manager && manager.emp_id) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/managers/info?manager_id=${manager.emp_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data && response.data.manager) {
          freshManagerData = response.data.manager;
          console.log('✅ [Refetch] Got manager by manager_id:', freshManagerData.name);
        }
      } catch (error) {
        console.log('⚠️ [Refetch] Could not fetch by manager_id:', error.message);
      }
    }
    
    // Method 3: Try by email
    if (!freshManagerData && manager && manager.email) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/managers/info?email=${manager.email}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data && response.data.manager) {
          freshManagerData = response.data.manager;
          console.log('✅ [Refetch] Got manager by email:', freshManagerData.name);
        }
      } catch (error) {
        console.log('⚠️ [Refetch] Could not fetch by email:', error.message);
      }
    }

    // Update state if we got new data
    if (freshManagerData) {
      // Merge with existing manager data to preserve all fields
      const updatedManager = {
        ...manager,
        ...freshManagerData,
        // Ensure profileImageUrl is set correctly
        profileImageUrl: freshManagerData.profileImageUrl || manager.profileImageUrl
      };
      
      setManager(updatedManager);
      sessionStorage.setItem("managerData", JSON.stringify(updatedManager));
      
      // Force image refresh by changing version
      setImageVersion(prev => prev + 1);
      
      console.log('✅ [Refetch] Manager data updated successfully');
      console.log('📸 [Refetch] Profile Image URL:', updatedManager.profileImageUrl);
      
      return updatedManager;
    } else {
      console.warn('⚠️ [Refetch] Could not refetch manager data, using cache busting');
      // Even if we can't refetch, force image refresh
      setImageVersion(prev => prev + 1);
    }
  } catch (err) {
    console.error('❌ [Refetch] Error:', err);
    // Still force image refresh on error
    setImageVersion(prev => prev + 1);
  }
};

  // Handle remove profile image
  const handleRemoveImage = async () => {
    if (!window.confirm('Are you sure you want to remove your profile image?')) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/managers/remove-profile-image`, 
        { managerId: manager._id }
      );

      if (response.data.success) {
        alert('Profile image removed successfully!');
        await refetchManagerData();
      } else {
        throw new Error(response.data.message || 'Failed to remove image');
      }
    } catch (error) {
      console.error('Error removing profile image:', error);
      alert('Remove failed: ' + (error.response?.data?.message || error.message));
    }
  };

  // Trigger file input click
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle leave approve/reject
  const handleQuickLeaveAction = async (leaveId, action, rejectReason = "") => {
    setLeaveRequests((prev) =>
      prev.map((leave) =>
        leave._id === leaveId
          ? { ...leave, status: action, rejectReason: rejectReason || "" }
          : leave
      )
    );

    try {
      const body = { status: action };
      if (action.toLowerCase() === "rejected") body.rejectReason = rejectReason || "No reason provided";

      const res = await axios.patch(`${API_BASE_URL}/api/leave/${leaveId}`, body);

      if (!res.data.success) {
        alert(res.data.message || "Failed to update leave. Try again.");
      }
    } catch (err) {
      console.error("Error updating leave:", err);
      alert("Server error while updating leave");
    }
  };

  // Handle timesheet update
  const handleTimesheetUpdate = (updatedTimesheet) => {
    setTimesheets((prev) => {
      const exists = prev.find((t) => t._id === updatedTimesheet._id);
      if (exists) return prev.map((t) => (t._id === updatedTimesheet._id ? updatedTimesheet : t));
      return [updatedTimesheet, ...prev];
    });
  };

  // Logout
  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/managerlogin");
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  };

  // Function to refresh dashboard data
  const refreshDashboardData = async () => {
    console.log("🔄 [Refresh] Refreshing dashboard data...");
    
    // Reset fetch flags
    hasFetchedTimesheets.current = false;
    hasFetchedLeaves.current = false;
    hasFetchedEmployees.current = false;
    
    if (manager && manager._id) {
      // Force re-fetch all data
      await fetchAssignedEmployees();
    }
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Enhanced tab switching handler
  const handleTabSwitch = (tabKey) => {
    console.log(`🔄 Switching to tab: ${tabKey}`);
    
    // If we're stuck, force cleanup
    if (isTabSwitchBlocked) {
      console.log("⚠️ Tab switching was blocked, forcing cleanup...");
      
      // Force cleanup of any blocking elements
      document.querySelectorAll('.modal, .modal-overlay, .modal-backdrop').forEach(el => {
        el.style.display = 'none';
      });
      
      // Reset body styles
      document.body.style.overflow = 'auto';
      document.body.style.pointerEvents = 'auto';
      
      // Clear any focus traps
      document.activeElement?.blur();
      
      setIsTabSwitchBlocked(false);
    }
    
    // Set the new active section
    setActiveSection(tabKey);
    setLastActiveTab(tabKey);
    setMobileMenuOpen(false);
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    // Add a small delay and check if we're stuck
    setTimeout(() => {
      if (activeSection === tabKey) {
        console.log("✅ Tab switch successful");
      } else {
        console.log("❌ Tab switch may be blocked");
        setIsTabSwitchBlocked(true);
      }
    }, 100);
  };

  // Add emergency exit function
  const emergencyExitTasks = () => {
    console.log("🚨 Emergency exit from tasks tab");
    
    // Force cleanup
    document.querySelectorAll('.modal, .modal-overlay, .modal-backdrop').forEach(el => {
      el.style.display = 'none';
    });
    
    document.body.style.overflow = 'auto';
    document.body.style.pointerEvents = 'auto';
    
    // Switch to overview tab
    setActiveSection('overview');
    setIsTabSwitchBlocked(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="manager-loading-container">
        <div className="manager-loading-spinner"></div>
        <p className="manager-loading-text">Loading Your Dashboard...</p>
      </div>
    );
  }

  // Error state - manager not found
  if (!manager) {
    return (
      <div className="manager-error-container">
        <div className="manager-error-content">
          <h2>Manager Not Found</h2>
          <p>The manager data could not be loaded.</p>
          <button className="manager-primary-btn" onClick={() => navigate("/managerlogin")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const managerProfilePic = getProfileImageUrl(manager);

  return (
    <div className="mano-container">
      {/* Emergency exit button (only shows when stuck) */}
     
      
      {/* Top Header */}
      <header className="mano-header">
        <div className="mano-header-content">
          <button className="mano-menu-toggle" onClick={toggleMobileMenu}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="mano-logo">
            <div className="mano-logo-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="mano-logo-text">
              <span className="mano-logo-main">{manager.company_name}</span>
              <span className="mano-logo-sub">Manager Portal</span>
            </span>
          </div>
          
          <div className="mano-header-right">
            <button 
              className="mano-refresh-btn"
              onClick={refreshDashboardData}
              title="Refresh Data"
              disabled={timesheetLoading || leaveLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            
            <div className="mano-user-profile">
              <div className="mano-user-avatar">
                {managerProfilePic ? (
                  <img 
                    src={managerProfilePic} 
                    alt="Profile" 
                    className="mano-user-avatar-img"
                  />
                ) : (
                  <div className="mano-user-avatar-initial">
                    {getInitials(manager.name)}
                  </div>
                )}
              </div>
              <div className="mano-user-info">
                <span className="mano-user-name">{manager.name}</span>
                <span className="mano-user-role">{manager.department} Manager</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="mano-main-layout">
        {/* Sidebar */}
        <aside className={`mano-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="mano-sidebar-header">
            <div className="mano-sidebar-user">
              <div className="mano-sidebar-avatar">
                {managerProfilePic ? (
                  <img 
                    src={managerProfilePic} 
                    alt="Profile" 
                    className="mano-sidebar-avatar-img"
                  />
                ) : (
                  <div className="mano-sidebar-avatar-initial">
                    {getInitials(manager.name)}
                  </div>
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="mano-sidebar-user-details">
                  <h4 className="mano-sidebar-user-name">{manager.name}</h4>
                  <p className="mano-sidebar-user-role">{manager.department} Manager</p>
                  <p className="mano-sidebar-user-company">{manager.company_name}</p>
                </div>
              )}
            </div>
            
            <button className="mano-sidebar-toggle" onClick={toggleSidebar}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                {sidebarCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>

          <nav className="mano-sidebar-nav">
            {menuItems.map((section, index) => (
              <div key={index} className="mano-nav-section">
                {!sidebarCollapsed && (
                  <div className="mano-nav-section-title">
                    {section.icon}
                    <span>{section.category}</span>
                  </div>
                )}
                
                <div className="mano-nav-items">
                  {section.items.map((item) => (
                    <button
                      key={item.key}
                      className={`mano-nav-item ${activeSection === item.key ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleTabSwitch(item.key);
                      }}
                      onMouseDown={(e) => {
                        // Prevent default to avoid any focus traps
                        e.preventDefault();
                      }}
                    >
                      <div className="mano-nav-item-icon">
                        {(() => {
                          switch(item.key) {
                            case 'overview':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                              );
                            case 'profile':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              );
                            case 'team':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13 0a5.002 5.002 0 00-10 0" />
                                </svg>
                              );
                            case 'attendance':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              );
                            case 'timesheets':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              );
                            case 'leave':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              );
                            case 'tasks':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              );
                            case 'manager-leaves':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              );
                            case 'meetings':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              );
                            case 'chats':
                              return (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                              );
                            default:
                              return null;
                          }
                        })()}
                      </div>
                      
                      {!sidebarCollapsed && (
                        <span className="mano-nav-item-label">{item.label}</span>
                      )}
                      
                      {!sidebarCollapsed && (
                        <div className="mano-nav-item-badge">
                          {item.key === 'leave' && stats.pendingLeave > 0 && (
                            <span className="mano-nav-badge">{stats.pendingLeave}</span>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="mano-sidebar-footer">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            <button 
              className="mano-upload-photo-btn"
              onClick={handleAvatarClick}
              disabled={uploadingImage}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {!sidebarCollapsed && <span>{uploadingImage ? 'Uploading...' : 'Upload Photo'}</span>}
            </button>
            
            <button 
              className="mano-logout-btn"
              onClick={handleLogout}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {!sidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="mano-content">
          {/* Content Header */}
          <div className="mano-content-header">
            <h1 className="mano-content-title">
              {activeSection === 'overview' && 'Dashboard Overview'}
              {activeSection === 'profile' && 'My Profile'}
              {activeSection === 'team' && 'Team Management'}
              {activeSection === 'attendance' && 'Attendance Management'}
              {activeSection === 'timesheets' && 'Timesheets Management'}
              {activeSection === 'leave' && 'Leave Management'}
              {activeSection === 'tasks' && 'Task Management'}
              {activeSection === 'meetings' && 'Meetings Management'}
              {activeSection === 'manager-leaves' && 'My Leave Applications'}
              {/* {activeSection === 'chats' && 'Team Chat'} */}
            </h1>
            
            <div className="mano-content-actions">
              {activeSection === 'tasks' && (
                <button 
                  className="mano-action-btn mano-action-btn-warning"
                  onClick={emergencyExitTasks}
                  style={{ marginRight: '10px' }}
                >
                  Exit Tasks
                </button>
              )}
              <button 
                className="mano-action-btn mano-action-btn-secondary"
                onClick={refreshDashboardData}
                disabled={timesheetLoading || leaveLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {timesheetLoading || leaveLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Content Body */}
          <div className="mano-content-body">
            <Suspense fallback={
              <div className="mano-loading-section">
                <div className="mano-loader-spinner"></div>
                <p>Loading module...</p>
              </div>
            }>
              {activeSection === "overview" && (
                <div className="mano-dashboard-overview">
                  {/* Welcome Section */}
                  <div className="mano-welcome-card">
                    <div className="mano-welcome-content">
                      <h2>Welcome back, {manager.name}!</h2>
                      <p>Here's what's happening with your team today.</p>
                    </div>
                    <div className="mano-welcome-stats">
                      <div className="mano-stat-item">
                        <span className="mano-stat-number">{stats.totalTeam}</span>
                        <span className="mano-stat-label">Team Members</span>
                      </div>
                      <div className="mano-stat-item">
                        <span className="mano-stat-number">{stats.pendingLeave}</span>
                        <span className="mano-stat-label">Pending Leaves</span>
                      </div>
                      <div className="mano-stat-item">
                        <span className="mano-stat-number">{stats.presentToday}</span>
                        <span className="mano-stat-label">Present Today</span>
                      </div>
                      <div className="mano-stat-item">
                        <span className="mano-stat-number">{stats.completedTasks}</span>
                        <span className="mano-stat-label">Completed Tasks</span>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="mano-stats-grid">
                    <div className="mano-stat-card mano-stat-card-primary">
                      <div className="mano-stat-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="mano-stat-content">
                        <h3>{stats.totalTeam}</h3>
                        <p>Team Members</p>
                      </div>
                    </div>

                    <div className="mano-stat-card mano-stat-card-warning">
                      <div className="mano-stat-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="mano-stat-content">
                        <h3>{stats.pendingLeave}</h3>
                        <p>Pending Leave Requests</p>
                      </div>
                    </div>

                    <div className="mano-stat-card mano-stat-card-success">
                      <div className="mano-stat-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="mano-stat-content">
                        <h3>{stats.presentToday}</h3>
                        <p>Present Today</p>
                      </div>
                    </div>

                    <div className="mano-stat-card mano-stat-card-info">
                      <div className="mano-stat-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="mano-stat-content">
                        <h3>{stats.completedTasks}</h3>
                        <p>Completed Tasks</p>
                      </div>
                    </div>
                  </div>

                  {/* Performance Section */}
                  <div className="mano-performance-section">
                    <div className="mano-section-header">
                      <h3>Team Performance</h3>
                      <button 
                        className="mano-action-btn mano-action-btn-secondary"
                        onClick={() => handleTabSwitch('tasks')}
                      >
                        View All Tasks
                      </button>
                    </div>

                    {Object.values(employeePerformance).length === 0 ? (
                      <div className="mano-empty-state">
                        <div className="mano-empty-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <h4>No Performance Data</h4>
                        <p>Assign tasks to see team performance metrics</p>
                      </div>
                    ) : (
                      <div className="mano-performance-grid">
                        {Object.values(employeePerformance).slice(0, 4).map((emp, index) => (
                          <div key={index} className="mano-performance-card">
                            <div className="mano-performance-header">
                              <div className="mano-performance-avatar">
                                {profileImages[emp._id] ? (
                                  <img 
                                    src={profileImages[emp._id]} 
                                    alt={emp.name}
                                    className="mano-performance-avatar-img"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className="mano-performance-avatar-initial">
                                  {emp.name?.charAt(0).toUpperCase() || "E"}
                                </div>
                              </div>
                              <div className="mano-performance-info">
                                <h4>{emp.name}</h4>
                                <p>{emp.email || emp.emp_id}</p>
                              </div>
                            </div>
                            
                            <div className="mano-performance-progress">
                              <div className="mano-progress-header">
                                <span>Completion Rate</span>
                                <span>{emp.percentage || 0}%</span>
                              </div>
                              <div className="mano-progress-bar">
                                <div 
                                  className="mano-progress-fill"
                                  style={{ width: `${emp.percentage || 0}%` }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="mano-performance-stats">
                              <div className="mano-perf-stat">
                                <span className="mano-perf-count">{emp.completed || 0}</span>
                                <span>Completed</span>
                              </div>
                              <div className="mano-perf-stat">
                                <span className="mano-perf-count">{emp.inProgress || 0}</span>
                                <span>In Progress</span>
                              </div>
                              <div className="mano-perf-stat">
                                <span className="mano-perf-count">{emp.pending || 0}</span>
                                <span>Pending</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === "profile" && (
                <div className="mano-profile-section">
                  <div className="mano-profile-card">
                    <div className="mano-profile-header">
                      <div className="mano-profile-avatar">
                        {managerProfilePic ? (
                          <img 
                            src={managerProfilePic} 
                            alt="Profile" 
                            className="mano-profile-avatar-img"
                          />
                        ) : (
                          <div className="mano-profile-avatar-initial">
                            {getInitials(manager.name)}
                          </div>
                        )}
                        <button 
                          className="mano-avatar-change-btn"
                          onClick={handleAvatarClick}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? 'Uploading...' : 'Change Photo'}
                        </button>
                      </div>
                      
                      <div className="mano-profile-info">
                        <h2>{manager.name}</h2>
                        <p className="mano-profile-role">{manager.department} Manager</p>
                        <p className="mano-profile-company">{manager.company_name}</p>
                        
                        <div className="mano-profile-stats">
                          <div className="mano-profile-stat">
                            <span className="mano-profile-stat-value">{stats.totalTeam}</span>
                            <span className="mano-profile-stat-label">Team Members</span>
                          </div>
                          <div className="mano-profile-stat">
                            <span className="mano-profile-stat-value">{stats.pendingLeave}</span>
                            <span className="mano-profile-stat-label">Pending Leaves</span>
                          </div>
                          <div className="mano-profile-stat">
                            <span className="mano-profile-stat-value">{stats.completedTasks}</span>
                            <span className="mano-profile-stat-label">Completed Tasks</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mano-profile-details">
                      <div className="mano-detail-section">
                        <h3>Personal Information</h3>
                        <div className="mano-detail-grid">
                          <div className="mano-detail-item">
                            <label>Full Name</label>
                            <p>{manager.name || "Not provided"}</p>
                          </div>
                          <div className="mano-detail-item">
                            <label>Email</label>
                            <p>{manager.email || "Not provided"}</p>
                          </div>
                          <div className="mano-detail-item">
                            <label>Phone</label>
                            <p>{manager.phone || "Not provided"}</p>
                          </div>
                          <div className="mano-detail-item">
                            <label>Department</label>
                            <p>{manager.department || "Not assigned"}</p>
                          </div>
                          <div className="mano-detail-item">
                            <label>Company</label>
                            <p>{manager.company_name || "Not assigned"}</p>
                          </div>
                          <div className="mano-detail-item">
                            <label>Employee ID</label>
                            <p>{manager.emp_id || "Not assigned"}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mano-detail-section">
                        <h3>Team Members ({assignedEmployees.length})</h3>
                        {assignedEmployees.length === 0 ? (
                          <div className="mano-empty-team">
                            <p>No team members assigned yet.</p>
                          </div>
                        ) : (
                          <div className="mano-team-grid">
                            {assignedEmployees.slice(0, 6).map((emp, index) => (
                              <div key={index} className="mano-team-member">
                                <div className="mano-team-avatar">
                                  {profileImages[emp._id] ? (
                                    <img 
                                      src={profileImages[emp._id]} 
                                      alt={emp.name}
                                      className="mano-team-avatar-img"
                                    />
                                  ) : (
                                    <div className="mano-team-avatar-initial">
                                      {emp.name?.charAt(0).toUpperCase() || "E"}
                                    </div>
                                  )}
                                </div>
                                <div className="mano-team-info">
                                  <h4>{emp.name}</h4>
                                  <p>{emp.role || "Employee"}</p>
                                  <small>ID: {emp.emp_id || "N/A"}</small>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "team" && (
                <TeamManagementManager
                  manager={manager}
                  assignedEmployees={assignedEmployees}
                  fetchAssignedEmployees={fetchAssignedEmployees}
                />
              )}

              {activeSection === "attendance" && (
                <AttendanceManagementManager manager={manager} assignedEmployees={assignedEmployees} />
              )}

              {activeSection === "timesheets" && (
                <TimesheetManager
                  manager={manager}
                  assignedEmployees={assignedEmployees}
                  timesheets={timesheets}
                  onTimesheetUpdate={handleTimesheetUpdate}
                />
              )}

              {activeSection === "manager-leaves" && (
                <ManagerLeaveApplication 
                  manager={manager}
                  assignedEmployees={assignedEmployees}
                />
              )}

              {activeSection === "leave" && (
                <LeaveManagementManager
                  assignedEmployees={assignedEmployees}
                  leaveRequests={leaveRequests}
                  leaveLoading={leaveLoading}
                  handleQuickLeaveAction={handleQuickLeaveAction}
                  fetchTeamLeaveRequests={() => {}}
                />
              )}

              {activeSection === "tasks" && (
                <TaskManagementManager 
                  key={`tasks-${Date.now()}`}
                  manager={manager}
                  assignedEmployees={assignedEmployees}
                  refreshDashboard={refreshDashboardData}
                  onEmergencyExit={emergencyExitTasks}
                />
              )}

              {activeSection === "meetings" && (
                <ManagerMeetings manager={manager} assignedEmployees={assignedEmployees} />
              )}

              {activeSection === "chats" && (
                <ManagerChats
                  manager={manager}
                  assignedEmployees={assignedEmployees}
                />
              )}
            </Suspense>
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mano-mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}
    </div>
  );
};

export default ManagerDashboard;